/**
 * Scanner diagnostics, pure part. Answers one question for a person standing next to a handheld: "does the
 * browser get anything from the scanner at all, and if it does, why is it (not) taken for a scan?"
 *
 * The decisions come from the SAME `KeyStreamAssembler` and the SAME normalisation the production scanner uses
 * (`@wms/scan-input`), so the verdict cannot drift from real behaviour. No business data is read or sent: only
 * key/input event metadata is recorded, and barcode characters are masked when an event is recorded.
 */
import { KeyStreamAssembler, normalizeScanValue, type AssembleOutcome } from '@wms/scan-input';
import { DEFAULT_SCAN_CONFIG, keyToChar, type ScanConfig } from './scanCore';

export type RejectReason =
  | 'NO_RUN' // Enter/Tab with nothing before it
  | 'TOO_SHORT' // fewer characters than minLength
  | 'TERMINATOR_LATE' // Enter/Tab came later than terminatorGapMs after the last character
  | 'NO_SUFFIX' // characters arrived, no Enter/Tab followed (and too short for the idle commit)
  | 'UNMAPPED_KEY'; // key the engine cannot turn into a barcode character (Unidentified/Process/non-Latin without code)

export type DiagKind = 'keydown' | 'keyup' | 'beforeinput' | 'input' | 'paste';

export type DiagEvent = {
  readonly n: number;
  readonly kind: DiagKind;
  readonly at: number;
  /** ms since the previous recorded event of any kind, null for the first. */
  readonly dtMs: number | null;
  readonly key: string;
  readonly code: string;
  readonly keyCode: number;
  readonly inputType: string;
  readonly dataLength: number;
  readonly targetTag: string;
  readonly composing: boolean;
  readonly trusted: boolean;
  /** Short human note, e.g. why this key was or was not taken into the run. */
  readonly note: string;
};

export type DiagVerdict =
  | { readonly kind: 'ACCEPTED'; readonly length: number; readonly suffix: 'Enter' | 'Tab' | 'None (idle commit)'; readonly maxGapMs: number }
  | { readonly kind: 'REJECTED'; readonly reason: RejectReason; readonly detail: string };

export type RawKey = {
  readonly key: string;
  readonly code: string;
  readonly keyCode: number;
  readonly shiftKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly altKey?: boolean;
  readonly metaKey?: boolean;
  readonly composing?: boolean;
  readonly targetTag: string;
  /** True when the focused element is one a person types into (production global capture ignores it). */
  readonly typingTarget: boolean;
  readonly trusted: boolean;
};

export type Counters = {
  readonly keyEvents: number;
  readonly inputEvents: number;
  readonly scanCandidates: number;
  readonly acceptedScans: number;
};

/** Keys that are not barcode characters: safe to show as they are. */
const NON_CHAR_CODE = /^(Enter|NumpadEnter|Tab|Shift|Control|Alt|Meta|Arrow|Backspace|Delete|Escape|CapsLock|F\d|Page|Home|End|Insert|Space|Unidentified|$)/;
const NON_CHAR_KEYCODES = new Set([0, 8, 9, 13, 16, 17, 18, 20, 27, 32, 33, 34, 35, 36, 37, 38, 39, 40, 45, 46, 229]);
export const MASK = '<char>';

/** `event.key` with printable characters hidden: the log must never reveal what was scanned. */
export function maskKey(key: string): string {
  return [...key].length === 1 && key !== ' ' ? MASK : key;
}
export function maskCode(code: string): string {
  return NON_CHAR_CODE.test(code) ? code : MASK;
}
export function maskKeyCode(keyCode: number): number {
  return NON_CHAR_KEYCODES.has(keyCode) ? keyCode : 0;
}

export const NO_SUFFIX_IDLE_MS = 1000;
export const NO_EVENTS_VERDICT_MS = 10_000;

export class ScanDiagnostics {
  readonly #cfg: ScanConfig;
  readonly #asm: KeyStreamAssembler;
  #lastKeyAt: number | null = null;
  #lastEventAt: number | null = null;
  #seq = 0;
  #keyEvents = 0;
  #inputEvents = 0;
  #candidates = 0;
  #accepted = 0;
  #noSuffixReported = false;
  #events: DiagEvent[] = [];
  #verdicts: DiagVerdict[] = [];

  constructor(cfg: Partial<ScanConfig> = {}) {
    this.#cfg = { ...DEFAULT_SCAN_CONFIG, ...cfg };
    this.#asm = new KeyStreamAssembler(this.#cfg);
  }

  get config(): ScanConfig {
    return this.#cfg;
  }

  get counters(): Counters {
    return { keyEvents: this.#keyEvents, inputEvents: this.#inputEvents, scanCandidates: this.#candidates, acceptedScans: this.#accepted };
  }

  /** How many characters the current run holds. The characters themselves are never exposed. */
  get bufferLength(): number {
    return this.#asm.length;
  }

  get events(): readonly DiagEvent[] {
    return this.#events;
  }

  get verdicts(): readonly DiagVerdict[] {
    return this.#verdicts;
  }

  /** The longest gap between characters of the last accepted scan (timing diagnostics), or null. */
  get lastAcceptedMaxGapMs(): number | null {
    for (let i = this.#verdicts.length - 1; i >= 0; i -= 1) {
      const v = this.#verdicts[i]!;
      if (v.kind === 'ACCEPTED') return v.maxGapMs;
    }
    return null;
  }

  #record(kind: DiagKind, at: number, fields: Partial<DiagEvent>): DiagEvent {
    const dtMs = this.#lastEventAt === null ? null : Math.round((at - this.#lastEventAt) * 10) / 10;
    this.#lastEventAt = at;
    const ev: { -readonly [K in keyof DiagEvent]: DiagEvent[K] } = {
      n: ++this.#seq, kind, at, dtMs,
      key: '', code: '', keyCode: 0, inputType: '', dataLength: 0, targetTag: '', composing: false, trusted: true, note: '',
      ...fields,
    };
    // Whatever the caller passed, what is stored and shown never carries a barcode character.
    ev.key = maskKey(ev.key);
    ev.code = maskCode(ev.code);
    ev.keyCode = maskKeyCode(ev.keyCode);
    this.#events = [...this.#events.slice(-199), ev];
    return ev;
  }

  #verdict(v: DiagVerdict): DiagVerdict {
    this.#verdicts = [...this.#verdicts.slice(-49), v];
    return v;
  }

  keyup(at: number, k: RawKey): void {
    this.#record('keyup', at, { key: k.key, code: k.code, keyCode: k.keyCode, targetTag: k.targetTag, composing: k.composing === true, trusted: k.trusted });
  }

  input(kind: 'beforeinput' | 'input' | 'paste', at: number, info: { inputType: string; dataLength: number; targetTag: string; composing: boolean; trusted: boolean }): void {
    if (kind === 'input' || kind === 'paste') this.#inputEvents += 1;
    this.#record(kind, at, { ...info, note: kind === 'paste' ? 'вставка текста (paste)' : '' });
  }

  /** One keydown. Returns the verdict if this key ended a run (Enter/Tab), else null. */
  keydown(at: number, k: RawKey): DiagVerdict | null {
    this.#keyEvents += 1;
    const base = { key: k.key, code: k.code, keyCode: k.keyCode, targetTag: k.targetTag, composing: k.composing === true, trusted: k.trusted };

    if (k.typingTarget) {
      // Production global capture steps aside for a focused field; the field submits on Enter by itself.
      this.#asm.reset();
      this.#record('keydown', at, { ...base, note: k.key === 'Enter' || k.key === 'Tab' ? 'поле ввода: код принимается по Enter без проверки скорости' : 'поле ввода: глобальный перехват не участвует' });
      return null;
    }

    if (k.key === 'Enter' || k.key === 'Tab') {
      const suffix = k.key;
      const hadRun = this.#asm.length > 0;
      if (hadRun) this.#candidates += 1;
      const out = this.#asm.terminate(at, suffix === 'Enter' ? 'ENTER' : 'TAB');
      this.#noSuffixReported = true;
      return this.#settle(out, at, base, suffix);
    }

    const c = keyToChar({ key: k.key, code: k.code, shiftKey: k.shiftKey, ctrlKey: k.ctrlKey, altKey: k.altKey, metaKey: k.metaKey });
    if (c === null) {
      const unmapped = k.key === 'Unidentified' || k.key === 'Process' || k.key === 'Dead' || (k.key.length === 1 && !k.ctrlKey && !k.altKey && !k.metaKey);
      this.#record('keydown', at, { ...base, note: unmapped ? 'символ не распознан (key/code не дают латинский знак)' : 'служебная клавиша, не символ' });
      if (unmapped) this.#verdict({ kind: 'REJECTED', reason: 'UNMAPPED_KEY', detail: `key="${maskKey(k.key)}" code="${maskCode(k.code)}"` });
      return null;
    }
    const before = this.#asm.droppedRuns;
    const prevAt = this.#asm.lastCharAt;
    this.#asm.setConfig(this.#cfg);
    this.#asm.pushChar(c, at);
    const broke = this.#asm.droppedRuns !== before;
    this.#noSuffixReported = false;
    this.#lastKeyAt = at;
    const gap = prevAt === null ? 0 : at - prevAt;
    this.#record('keydown', at, {
      ...base,
      note: broke ? `пауза ${Math.round(gap)} мс > ${this.#cfg.interCharMs} мс: начат новый прогон` : prevAt === null || this.#asm.length === 1 ? 'начало прогона' : `в прогон (+${Math.round(gap)} мс)`,
    });
    return null;
  }

  #settle(out: AssembleOutcome, at: number, base: Partial<DiagEvent>, suffix: 'Enter' | 'Tab'): DiagVerdict {
    if (out.kind === 'SCAN') {
      this.#accepted += 1;
      const n = normalizeScanValue(out.value);
      const v = this.#verdict({ kind: 'ACCEPTED', length: n.ok ? n.value.length : out.value.length, suffix, maxGapMs: out.maxGapMs });
      this.#record('keydown', at, { ...base, note: `суффикс ${suffix}: СКАН ПРИНЯТ (${out.value.length} симв.)` });
      return v;
    }
    let reason: RejectReason;
    let detail: string;
    if (out.kind === 'IGNORED') {
      reason = 'NO_RUN';
      detail = `${suffix} без символов перед ним`;
    } else if (out.reason === 'TOO_SHORT') {
      reason = 'TOO_SHORT';
      detail = `${out.length} симв., нужно не меньше ${this.#cfg.minLength}`;
    } else {
      reason = 'TERMINATOR_LATE';
      detail = `${suffix} пришёл слишком поздно после последнего символа (${out.detail})`;
    }
    const v = this.#verdict({ kind: 'REJECTED', reason, detail });
    this.#record('keydown', at, { ...base, note: `суффикс ${suffix}: отклонён - ${reason}` });
    return v;
  }

  /**
   * Call periodically with the current time. A fast, long-enough run with no suffix is committed (idle commit,
   * same as production); a shorter run that stops without Enter/Tab is reported once as NO_SUFFIX.
   */
  tick(now: number): DiagVerdict | null {
    if (this.#asm.length === 0 || this.#lastKeyAt === null || this.#noSuffixReported) return null;
    this.#asm.setConfig(this.#cfg);
    const idle = this.#asm.tick(now);
    if (idle !== null && idle.kind === 'SCAN') {
      this.#noSuffixReported = true;
      this.#candidates += 1;
      this.#accepted += 1;
      const n = normalizeScanValue(idle.value);
      return this.#verdict({ kind: 'ACCEPTED', length: n.ok ? n.value.length : idle.value.length, suffix: 'None (idle commit)', maxGapMs: idle.maxGapMs });
    }
    if (now - this.#lastKeyAt < NO_SUFFIX_IDLE_MS) return null;
    this.#noSuffixReported = true;
    const n = this.#asm.length;
    const v = this.#verdict({
      kind: 'REJECTED',
      reason: 'NO_SUFFIX',
      detail: `${n} симв. пришли, но Enter/Tab не последовал: сканер без суффикса (или суффикс другой клавишей)`,
    });
    this.#asm.reset();
    return v;
  }

  reset(): void {
    this.#asm.reset();
    this.#lastKeyAt = null;
    this.#lastEventAt = null;
    this.#seq = 0;
    this.#keyEvents = 0;
    this.#inputEvents = 0;
    this.#candidates = 0;
    this.#accepted = 0;
    this.#noSuffixReported = false;
    this.#events = [];
    this.#verdicts = [];
  }
}

export type Diagnosis =
  | 'WAITING' // fewer than 10 s, nothing seen yet
  | 'NO_EVENTS' // 10 s of silence: the browser gets nothing from the device
  | 'INPUT_ONLY' // text reached an input without key events (IME / injected text)
  | 'KEYS_NO_SCAN' // key events arrive but nothing was accepted
  | 'WORKS'; // at least one scan accepted

/** The 10-second answer. Pure: counters + how long the page has been armed. */
export function diagnose(c: Counters, armedForMs: number): Diagnosis {
  if (c.acceptedScans > 0) return 'WORKS';
  if (c.keyEvents > 0) return 'KEYS_NO_SCAN';
  if (c.inputEvents > 0) return 'INPUT_ONLY';
  return armedForMs >= NO_EVENTS_VERDICT_MS ? 'NO_EVENTS' : 'WAITING';
}

export const DIAGNOSIS_TEXT: Record<Diagnosis, { readonly title: string; readonly body: string }> = {
  WAITING: {
    title: 'Ждём скан…',
    body: 'Нажмите курок сканера и считайте любой штрихкод. Если за 10 секунд ничего не придёт, страница скажет, что это значит.',
  },
  NO_EVENTS: {
    title: 'Браузер не получает НИЧЕГО от устройства',
    body: 'Это не ошибка браузерного кода: ОС не отдаёт скан браузеру. Скорее всего сканер работает в режиме Intent/Broadcast (вендорская служба) или выключен. В браузере Ops такой скан принять нельзя: работайте через приложение ТСД (оно умеет Android Broadcast) либо переключите вывод сканера на «клавиатура / Keystroke / HID wedge».',
  },
  INPUT_ONLY: {
    title: 'Текст приходит, но не клавишами',
    body: 'Поле ввода получает текст без событий клавиатуры (ввод через IME или службу устройства). Глобальный перехват такой ввод не видит; работает только поле ввода с кнопкой «Принять».',
  },
  KEYS_NO_SCAN: {
    title: 'События клавиатуры есть, но скан не засчитан',
    body: 'Сканер печатает клавишами, значит режим wedge работает. Причина в журнале ниже: нет суффикса Enter/Tab, слишком длинные паузы между символами, слишком короткий код или нераспознанные клавиши.',
  },
  WORKS: {
    title: 'Скан принят',
    body: 'Браузер получает клавиши сканера, и боевая логика распознаёт их как скан. Если приёмка всё равно не реагирует, причина не в устройстве.',
  },
};

/** Recommended mode for the browser (Ops has exactly one provider: keyboard wedge). */
export function recommendedBrowserMode(d: Diagnosis): string {
  switch (d) {
    case 'WORKS':
    case 'KEYS_NO_SCAN':
      return 'KEYBOARD (HID wedge): оставить как есть';
    case 'INPUT_ONLY':
      return 'KEYBOARD через поле ввода; для надёжной работы переключите сканер на клавиатурный вывод';
    case 'NO_EVENTS':
      return 'В браузере нет входа: используйте приложение ТСД (Android Broadcast) или клавиатурный вывод сканера';
    default:
      return 'ещё нет данных';
  }
}

/** Plain-text report for the partner to send back. Events are masked at record time: no barcode characters. */
export function buildReport(
  info: { readonly userAgent: string; readonly armedForMs: number; readonly cfg: ScanConfig },
  counters: Counters,
  events: readonly DiagEvent[],
  verdicts: readonly DiagVerdict[],
): string {
  const d = diagnose(counters, info.armedForMs);
  const lines: string[] = [];
  lines.push('WMS scanner check (Ops browser, provider: browser-keyboard)');
  lines.push(`diagnosis: ${d}`);
  lines.push(`recommended: ${recommendedBrowserMode(d)}`);
  lines.push(`KEY EVENTS: ${counters.keyEvents}`);
  lines.push(`INPUT EVENTS: ${counters.inputEvents}`);
  lines.push(`SCAN CANDIDATES: ${counters.scanCandidates}`);
  lines.push(`ACCEPTED SCANS: ${counters.acceptedScans}`);
  lines.push(`thresholds: minLength=${info.cfg.minLength} interCharMs=${info.cfg.interCharMs} terminatorGapMs=${info.cfg.terminatorGapMs} idleCommitMs=${info.cfg.idleCommitMs} duplicateWindowMs=${info.cfg.duplicateWindowMs}`);
  lines.push(`userAgent: ${info.userAgent}`);
  lines.push('last verdicts:');
  for (const v of verdicts.slice(-8)) {
    lines.push(v.kind === 'ACCEPTED' ? `  ACCEPTED len=${v.length} suffix=${v.suffix} maxGap=${Math.round(v.maxGapMs)}ms` : `  REJECTED ${v.reason}: ${v.detail}`);
  }
  lines.push('last events (kind key code keyCode dt target):');
  for (const e of events.slice(-25)) {
    lines.push(`  ${e.n} ${e.kind} key=${e.key} code=${e.code} keyCode=${e.keyCode} dt=${e.dtMs ?? '-'} target=${e.targetTag}${e.inputType ? ` inputType=${e.inputType}` : ''}`);
  }
  return lines.join('\n');
}
